import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { PhysicalOrdersService } from "@/services/physicalOrders.service";
import useModalStore from "@/store/useModalStore";
import { normalizeUrlWhenPaste } from "@/utils/normalize";
import { CARRIERS } from "@/utils/orders";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Order } from "../hooks/useOrders";
import { ShipmentSelector } from "./ShipmentSelector";

export const TRACKING_MODAL_NAME = "tracking-modal";

const trackingSchema = z.object({
  carrier: z.string().min(1, "required"),
  tracking_code: z.string().min(1, "required").max(100),
  tracking_url: z.string().url("invalid_url").max(500),
  delivery_estimate: z.string().min(1, "required").max(20),
  tracking_id: z.string().optional(),
});

type TrackingFormData = z.infer<typeof trackingSchema>;

interface TrackingModalProps {
  order: Order | null;
  onSuccess?: () => void;
}

export function TrackingModal({ order, onSuccess }: TrackingModalProps) {
  const { t } = useTranslation(["common", "orders"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const isOpen = modals[TRACKING_MODAL_NAME] || false;
  const requiresTrackingId = Boolean(order?.has_multiple_trackings);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<TrackingFormData>({
    resolver: zodResolver(trackingSchema),
    mode: "onChange",
    defaultValues: {
      carrier: "",
      tracking_code: "",
      tracking_url: "",
      delivery_estimate: "",
      tracking_id: "",
    },
  });

  const trackingUrl = watch("tracking_url");
  const trackingId = watch("tracking_id");

  useEffect(() => {
    if (order && isOpen) {
      reset({
        carrier: order.tracking?.carrier || "",
        tracking_code: order.tracking?.tracking_code || "",
        tracking_url: order.tracking?.tracking_url || "",
        delivery_estimate: "",
        tracking_id: "",
      });
    }
  }, [order, isOpen, reset]);

  const { mutate: saveTracking, isPending } = useMutation({
    mutationFn: async (data: TrackingFormData) => {
      if (!order) throw new Error("Order not found");
      if (requiresTrackingId && !data.tracking_id) {
        throw new Error(t("common:required_field"));
      }

      return PhysicalOrdersService.saveTracking(order.id.replace("#", ""), {
        tracking_code: data.tracking_code,
        carrier: data.carrier,
        tracking_url: data.tracking_url,
        delivery_estimate: data.delivery_estimate,
        tracking_id: data.tracking_id || undefined,
      });
    },
    onSuccess: () => {
      toast({
        description: t("orders:tracking.saved"),
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["physical-orders"] });
      queryClient.invalidateQueries({ queryKey: ["physical-orders-summary"] });
      queryClient.invalidateQueries({ queryKey: ["getSellInfo"] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    reset({
      carrier: "",
      tracking_code: "",
      tracking_url: "",
      delivery_estimate: "",
      tracking_id: "",
    });
    closeModal(TRACKING_MODAL_NAME);
  };

  const onSubmit = (data: TrackingFormData) => {
    saveTracking(data);
  };

  const canSubmit = isValid && (!requiresTrackingId || Boolean(trackingId));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("orders:tracking_modal.title")}</DialogTitle>
          <DialogDescription>
            {t("orders:tracking_modal.description", { orderId: order?.id })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 py-4">
            <ShipmentSelector
              trackings={order?.delivery_trackings || []}
              value={trackingId}
              onChange={(value) => setValue("tracking_id", value, { shouldValidate: true })}
              error={requiresTrackingId && !trackingId}
            />
            {/* Transportadora */}
            <div className="grid gap-2">
              <Label htmlFor="carrier">
                {t("orders:tracking_modal.carrier")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Controller
                name="carrier"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="carrier" className={errors.carrier ? "border-destructive" : ""}>
                      <SelectValue placeholder={t("orders:tracking_modal.select_carrier")} />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[300px]">
                      {CARRIERS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.value === "other" ? t("orders:tracking_modal.other") : c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.carrier && (
                <span className="text-xs text-destructive">
                  {t("common:required_field")}
                </span>
              )}
            </div>

            {/* Código de rastreio */}
            <div className="grid gap-2">
              <Label htmlFor="tracking_code">
                {t("orders:tracking_modal.tracking_code")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="tracking_code"
                {...register("tracking_code")}
                placeholder={t("orders:tracking_modal.tracking_code_placeholder")}
                className={errors.tracking_code ? "border-destructive" : ""}
              />
              {errors.tracking_code && (
                <span className="text-xs text-destructive">
                  {t("common:required_field")}
                </span>
              )}
            </div>

            {/* URL de rastreamento */}
            <div className="grid gap-2">
              <Label htmlFor="tracking_url">
                {t("orders:tracking_modal.tracking_url")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="tracking_url"
                {...register("tracking_url")}
                placeholder={t("orders:tracking_modal.tracking_url_placeholder")}
                className={errors.tracking_url ? "border-destructive" : ""}
                onFocus={(e) => {
                  if (!e.target.value) {
                    setValue("tracking_url", "https://", { shouldValidate: true });
                  }
                }}
                onPaste={(e) => {
                  const finalValue = normalizeUrlWhenPaste(e);
                  setValue("tracking_url", finalValue, { shouldValidate: true });
                }}
              />
              {errors.tracking_url && (
                <span className="text-xs text-destructive">
                  {errors.tracking_url.message === "invalid_url"
                    ? t("common:invalid_url")
                    : t("common:required_field")}
                </span>
              )}
            </div>

            {/* Data estimada de entrega */}
            <div className="grid gap-2">
              <Label htmlFor="delivery_estimate">
                {t("orders:tracking_modal.delivery_estimate")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="delivery_estimate"
                type="date"
                {...register("delivery_estimate")}
                placeholder={t("orders:tracking_modal.delivery_estimate_placeholder")}
                className={errors.delivery_estimate ? "border-destructive" : ""}
              />
              {errors.delivery_estimate && (
                <span className="text-xs text-destructive">
                  {t("common:required_field")}
                </span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit || isPending}>
              {isPending ? t("common:saving") : t("common:save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
